/**
 * The panel's view of public widgets, plus the snippet generator.
 *
 * TWO HALVES AND ONLY ONE OF THEM TOUCHES THE NETWORK. `embeds` is a thin client
 * over `/api/embeds`; `snippetFor` is a pure function. That split matters because
 * the snippet is the thing a customer pastes into their website, and it must be
 * derivable without a round trip — so the playground shows it changing live as
 * you edit, and so it can be reasoned about in a test.
 *
 * THE SNIPPET CARRIES THE KEY *AND* THE APPEARANCE (product owner, 2026-08-23:
 * the widgets are pure functions of client-supplied configuration). Every
 * appearance setting is inlined as a `data-` attribute on the script tag, so a
 * customer can hand-edit a colour or a label in their own HTML without touching
 * this panel, and what a page renders is exactly what that page says. The
 * earlier design stored appearance on the embed record server-side; that made
 * the widget a function of hidden state — a colour nobody could see in the page
 * source — and the record's config was write-only in practice (the playground
 * never even read it back). The DB row now holds only what MUST be server-side
 * because it is security or capacity, never appearance: the key, the agent, the
 * variant, the origin allowlist, the forwarded-params allowlist, and the caps.
 *
 * A mistyped attribute falls back to that option's default (and the widget warns
 * in the console where it can) — the acceptable cost of a page you can read.
 */
import { ApiError, req } from '@/api'

export type EmbedVariant = 'flag' | 'chat' | 'bar' | 'bars' | 'orb' | 'button' | 'pill'

export interface EmbedRow {
  id: string
  agentId: string
  name: string
  /**
   * `pk_…`. PUBLIC — it appears in the page source of the customer's website, so
   * displaying it in the panel and copying it to a clipboard are both fine.
   *
   * It is not a credential and nothing is authorised on the strength of it alone;
   * the server checks the calling origin against this embed's allowlist on every
   * request. The long version is in `packages/db/src/embeds.ts`.
   */
  publicKey: string
  variant: EmbedVariant
  config: Record<string, unknown>
  allowedOrigins: string[]
  /**
   * Query-string keys the widget may read off its host page's URL AT CLICK
   * TIME and forward as session params (for the agent's init webhook and HTTP
   * tools). SECURITY-RELEVANT THE SAME WAY `allowedOrigins` IS: the server
   * filters the submitted params against this list on every session create —
   * a hostile page can POST anything without loading our bundle — so this is
   * the entire allowlist, not a hint to the client. Keys only, ≤20, matched
   * byte-for-byte; empty means the widget reads nothing from the URL, which is
   * the default and was the only behaviour before this field existed.
   */
  forwardParams: string[]
  enabled: boolean
  maxSessionsPerDay: number
  createdAt: string
  revokedAt: string | null
  /** Sessions this widget has started today, against `maxSessionsPerDay`. */
  usedToday: number
}

export interface CreateEmbedBody {
  agentId: string
  name: string
  variant: EmbedVariant
  /** LEGACY — appearance rides the snippet's data-attributes now; the panel no
   *  longer sends this and new rows store `{}`. */
  config?: Record<string, unknown>
  allowedOrigins: string[]
  /** Omitted means none — the widget reads nothing off the host URL. */
  forwardParams?: string[]
  maxSessionsPerDay?: number
}

export type PatchEmbedBody = Partial<{
  name: string
  config: Record<string, unknown>
  allowedOrigins: string[]
  forwardParams: string[]
  enabled: boolean
  maxSessionsPerDay: number
}>

export const embeds = {
  /**
   * Every widget, plus where `embed.js` is served from.
   *
   * `scriptOrigin` comes back with the list rather than being a constant here,
   * because it is deployment configuration — and it is EDGE-API's origin, not the
   * panel's, so a constant in this bundle would be wrong on any deployment whose
   * workers are named differently. Empty means the deployment has not set
   * `EMBED_SCRIPT_ORIGIN`, and the playground should say so rather than render a
   * snippet with a hole in it.
   */
  list(signal: AbortSignal) {
    return req<{ rows: EmbedRow[]; scriptOrigin: string }>('/embeds', { signal })
  },
  create(body: CreateEmbedBody, signal: AbortSignal) {
    return req<EmbedRow>('/embeds', { signal, method: 'POST', body })
  },
  patch(id: string, body: PatchEmbedBody, signal: AbortSignal) {
    return req<EmbedRow>(`/embeds/${encodeURIComponent(id)}`, { signal, method: 'PATCH', body })
  },
  remove(id: string, signal: AbortSignal) {
    return req<void>(`/embeds/${encodeURIComponent(id)}`, { signal, method: 'DELETE' })
  },
}

export { ApiError }

// ============================================================================
// The snippet
// ============================================================================

/**
 * The snippet: one script tag carrying the key, the agent route, and every
 * appearance setting as its own attribute.
 *
 * `async` so it never blocks the host page's parse — a widget that delays somebody
 * else's first paint is a widget they remove. `defer` would also work and is
 * marginally worse here: it guarantees ordering against other deferred scripts,
 * which we neither need nor want to promise.
 *
 * NO `integrity` HASH, deliberately. A subresource-integrity attribute would pin
 * the bundle's hash, which sounds strictly better and would mean every deploy of
 * ours silently breaks every customer's page until they edit their HTML. The
 * trade-off is real and this is the wrong side of it for a widget that updates.
 *
 * HANDING THE WIDGET FACTS ABOUT THE VISITOR — documented here because this is
 * where an integrator reads. ONE channel: the `data-client-payload` attribute
 * on the script tag itself, a JSON object of string values, re-read at the
 * moment the visitor clicks start (so a page that fetches its user's identity
 * asynchronously can set it late):
 *
 *     script.setAttribute('data-client-payload', JSON.stringify({ userID: 'u123' }))
 *
 * One JSON attribute rather than one attribute per key, because attribute names
 * lowercase silently and `userID` must survive byte-for-byte. The widget reads
 * NOTHING else off the page — not even its URL — and the server bounds the
 * payload and forwards it only to the customer's own webhook and tools.
 */
/** Minimal HTML attribute-value escaping. Values are the operator's own config
 *  strings, but a label with a quote in it must not break out of the attribute. */
function escapeAttr(v: string): string {
  return v.replaceAll('&', '&amp;').replaceAll('"', '&quot;')
}

/**
 * The projected config as ordered `data-` attributes, kebab-cased the way the
 * widget's `script.dataset` reads them back (dataset camelCases: `data-end-label`
 * → `endLabel`). The ORB's gradient pair rides `data-accent` / `data-on-accent`
 * — that is what OrbWidget actually maps them to — so the `colors` object never
 * appears as an attribute of its own.
 */
function attrsFor(config: Record<string, unknown>): [string, string][] {
  const out: [string, string][] = []
  const push = (name: string, v: unknown) => {
    if (v === undefined || v === null || v === '') return
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out.push([`data-${name}`, String(v)])
    }
  }
  const colors = config['colors'] as { from?: unknown; to?: unknown } | undefined
  push('label', config['label'])
  push('end-label', config['endLabel'])
  // The chat flag's header heading. SEPARATE from `label`, which is the rotated
  // word on its 40 px tab — see the note on `ChatFlagProps.title`.
  push('title', config['title'])
  // The hover pill's revealed word. Its own key because `label` is the resting
  // word and both are painted, one under the other's wipe.
  push('hover-label', config['hoverLabel'])
  push('caption', config['caption'])
  push('accent', colors?.from ?? config['accent'])
  push('on-accent', colors?.to ?? config['onAccent'])
  push('theme', config['theme'])
  push('side', config['side'])
  push('offset-y', config['offsetY'])
  push('width', config['width'])
  push('height', config['height'])
  // The chat flag's window, which is a different box from the tab above it.
  push('panel-width', config['panelWidth'])
  push('panel-height', config['panelHeight'])
  push('placement', config['placement'])
  push('allow-text', config['allowText'])
  push('allow-mute', config['allowMute'])
  push('size', config['size'])
  // The conversation bar's own card. Three colours because that widget paints its
  // surface instead of reading theme tokens — see conversation-bar.tsx. `dataset`
  // camelCases these back to `surface`, `onSurface` and `surfaceBorder`.
  push('surface', config['surface'])
  push('on-surface', config['onSurface'])
  push('surface-border', config['surfaceBorder'])
  // The hover pill's 2px ring, which is a third colour rather than an alpha of
  // either of the other two — see hover-pill.tsx.
  push('border', config['border'])
  // …and whether it clicks when the pointer arrives.
  push('sound', config['sound'])
  push('orb-theme', config['orbTheme'])
  // The hover pill's meter bar count (2026-09-04). Its mute switch rides the
  // shared `allow-mute` key above.
  push('meter-bars', config['meterBars'])
  push('show-timer', config['showTimer'])
  // Bars. Every one of these is a number or a CSS colour the customer can edit in
  // their own HTML; the widget clamps each to a sane range rather than trusting it.
  push('bar-count', config['barCount'])
  push('particles', config['particles'])
  push('glow', config['glow'])
  push('button-size', config['buttonSize'])
  push('caption-gap', config['captionGap'])
  push('radius', config['radius'])
  push('background', config['background'])
  push('button-color', config['buttonColor'])
  push('icon-color', config['iconColor'])
  push('caption-color', config['captionColor'])
  return out
}

/** One attribute per line, so the customer can read and hand-edit each one.
 *  `data-agent-id` sits right after the key: the key is agent-agnostic and the
 *  TAG routes (2026-08-23), so which agent answers is the page's own, visible,
 *  editable choice. */
function scriptTag(
  base: string,
  publicKey: string,
  agentId: string,
  config: Record<string, unknown>,
  extra: [string, string][] = [],
): string {
  const attrs = [
    ['data-key', publicKey] as [string, string],
    ['data-agent-id', agentId] as [string, string],
    ...extra,
    ...attrsFor(config),
  ]
    .map(([k, v]) => `\n  ${k}="${escapeAttr(v)}"`)
    .join('')
  return `<script src="${base}/embed.js" async${attrs}\n></script>`
}

export function snippetFor(embed: EmbedRow, origin: string, config: Record<string, unknown>, agentId: string): string {
  const base = origin.replace(/\/+$/, '')
  return scriptTag(base, embed.publicKey, agentId, config)
}

/**
 * The inline snippet, for putting a widget in one specific place on a page.
 *
 * Returns null for the three variants that have no inline form, and all three
 * refusals are the same refusal: a widget defined by where it floats cannot be put
 * in a div. Both flags are tabs clamped to the edge of the viewport — the plain one
 * and the chat one alike, since the chat panel slides out of that same edge; the
 * conversation bar is, since 2026-08-24, a corner-parked call button that opens
 * sideways into the bar (`conversation-bar.tsx`). "An edge-clamped tab, in this div"
 * and "a corner launcher, in this div" are not things. The playground shows the
 * reason rather than a disabled toggle.
 *
 * `bars` is the mirror image of both — see `supportsFloating`.
 */
export function inlineSnippetFor(
  embed: EmbedRow,
  origin: string,
  config: Record<string, unknown>,
  agentId: string,
): string | null {
  if (!supportsInline(embed.variant)) return null
  const base = origin.replace(/\/+$/, '')
  const id = `voiceai-${embed.variant}`
  return `<div id="${id}"></div>\n${scriptTag(base, embed.publicKey, agentId, config, [['data-target', `#${id}`]])}`
}

/**
 * Is this origin covered by the embed's allowlist?
 *
 * Used by the playground to warn BEFORE somebody pastes a snippet that cannot work.
 * The single most common way this feature fails in practice is an empty or wrong
 * origin list, and the failure surfaces on the customer's website as a widget that
 * silently does not appear — so it is worth catching here, where there is a human
 * looking at a screen who can fix it.
 *
 * Exact comparison, matching the server. Anything looser here would be a lie about
 * what the server will accept.
 */
export function originAllowed(embed: EmbedRow, origin: string): boolean {
  return embed.allowedOrigins.includes(origin)
}

/** Whether a variant can be placed inline as well as floating. The two flags and
 *  the conversation bar are all defined by floating — see `inlineSnippetFor`. */
export function supportsInline(variant: EmbedVariant): boolean {
  return variant !== 'flag' && variant !== 'chat' && variant !== 'bar'
}

/**
 * Whether a variant has a floating form at all — the inverse of the two above.
 *
 * False only for `bars` (2026-08-24). It is a stage rather than a launcher: an
 * 800-wide spectrum pinned to a corner covers a third of a phone screen, has no
 * collapsed state to get out of the way, and every corner clipped its own centre
 * control. So the variant lost its `placement` setting instead of keeping a
 * control that could only produce a worse page, and the playground offers it the
 * inline snippet only.
 */
export function supportsFloating(variant: EmbedVariant): boolean {
  return variant !== 'bars'
}

/**
 * Human label for a variant, for headings and selects.
 *
 * TITLE CASE, AND THE SAME STRING THE PLAYGROUND'S TAB STRIP SHOWS. These are
 * product names — what a customer says on a call and what a support ticket has to
 * match — not sentences, so they are capitalised like names. They also used to
 * disagree with the playground, which called `flag` "Side flag" while this said
 * "Edge flag": two names for one variant is how a support conversation goes
 * sideways. "Side Flag" won because it is the one operators had been reading.
 */
export function variantLabel(variant: EmbedVariant): string {
  switch (variant) {
    case 'flag':
      return 'Side Flag'
    case 'chat':
      return 'Chat Flag'
    case 'bar':
      return 'Conversation Bar'
    case 'bars':
      return 'Bars Visualizer'
    case 'orb':
      return 'Voice Orb'
    case 'button':
      return 'Plain Button'
    case 'pill':
      return 'Hover Pill'
  }
}
